// src/pages/Wishlist.jsx
import React, { useEffect, useMemo, useCallback, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./styles/wishlist.css";

import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import {
  getUserProductWishlist,
  addProdToCart,
  removeWishlistProduct,
  getUserCart as fetchUserCart,
} from "../app/features/user/userSlice";

import { getAllColors } from "../app/features/color/colorSlice";
import { getAllSizes } from "../app/features/size/sizeSlice";
import { getPprices } from "../app/features/printPrice/printPriceSlice";

import VariantToast from "../components/VariantToast";

import product1 from "../images/products/stamp.jpg";

/* =========================
   SKELETON COMPONENT
   ========================= */
const WishlistSkeleton = ({ rows = 5 }) => {
  return (
    <div className="wl-skel">
      {Array.from({ length: rows }).map((_, i) => (
        <div className="wl-row wl-row--skel" key={i}>
          {/* Product */}
          <div className="wl-col wl-col--product">
            <div className="wl-skelBox wl-skelRemove" />

            <div className="wl-prod">
              <div className="wl-img wl-img--skel">
                <div className="wl-skelBox wl-skelImg" />
              </div>

              <div className="wl-prodText">
                <div className="wl-skelBox wl-skelLine wl-skelTitle" />
                <div className="wl-skelBox wl-skelLine wl-skelSub" />
              </div>
            </div>
          </div>

          {/* Price */}
          <div className="wl-col wl-col--price">
            <div className="wl-skelBox wl-skelLine wl-skelPrice" />
          </div>

          {/* Date */}
          <div className="wl-col wl-col--date">
            <div className="wl-skelBox wl-skelLine wl-skelDate" />
          </div>

          {/* View */}
          <div className="wl-col wl-col--stock">
            <div className="wl-skelBox wl-skelLine wl-skelView" />
          </div>

          {/* Action */}
          <div className="wl-col wl-col--action">
            <div className="wl-skelBox wl-skelBtn" />
          </div>
        </div>
      ))}
    </div>
  );
};

const stripMeasurements = (val) => {
  const s = String(val || "").trim();
  if (!s) return "";
  return s
    .replace(/\s*\([^)]*\b(mm|cm|in|inch|inches)\b[^)]*\)\s*$/i, "")
    .replace(/\s*\b(mm|cm|in|inch|inches)\b\s*$/i, "")
    .trim();
};

const formatUGX = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return "UGX 0";
  return `UGX ${Math.round(num).toLocaleString()}`;
};

const Wishlist = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // ✅ authSlice name is "auth"
  const { wishlist, isLoading, isError, message } = useSelector(
    (state) => state.auth
  );

  // ✅ global colors/sizes/prices
  const allColors = useSelector((state) => {
    const a = state?.color?.colors;
    const b = state?.colors?.colors;
    return (Array.isArray(a) ? a : null) ?? (Array.isArray(b) ? b : null) ?? [];
  });

  const allSizes = useSelector((state) => {
    const a = state?.size?.sizes;
    const b = state?.sizes?.sizes;
    return (Array.isArray(a) ? a : null) ?? (Array.isArray(b) ? b : null) ?? [];
  });

  const printPriceState = useSelector(
    (state) => state?.printPrice || state?.printPrices || {}
  );
  const printPrices = useMemo(() => {
    const arr = printPriceState?.printPrices;
    return Array.isArray(arr) ? arr : [];
  }, [printPriceState?.printPrices]);

  // ✅ Fetch wishlist on mount
  useEffect(() => {
    dispatch(getUserProductWishlist());
  }, [dispatch]);

  // ✅ load dropdown data + cart (needed for VariantToast + pricing)
  useEffect(() => {
    dispatch(getAllColors());
    dispatch(getAllSizes());
    dispatch(getPprices());
    dispatch(fetchUserCart());
  }, [dispatch]);

  // optional error toast
  useEffect(() => {
    if (isError && message) toast.error(message);
  }, [isError, message]);

  // ✅ normalize wishlist items
  const list = useMemo(() => {
    const raw = Array.isArray(wishlist)
      ? wishlist
      : Array.isArray(wishlist?.wishlist)
      ? wishlist.wishlist
      : Array.isArray(wishlist?.getaUser?.wishlist)
      ? wishlist.getaUser.wishlist
      : [];

    return raw
      .map((p) => {
        const id = p?._id || p?.id;
        if (!id) return null;

        const title = p?.title || p?.name || "Untitled";
        const slug = String(p?.slug || "").trim();
        const price = Number(p?.price ?? 0);

        const img =
          (Array.isArray(p?.images) && (p.images?.[0]?.url || p.images?.[0])) ||
          p?.image ||
          product1;

        const color =
          p?.color?.title ||
          p?.color?.name ||
          (Array.isArray(p?.color)
            ? p.color?.[0]?.title || p.color?.[0]?.name
            : "") ||
          "";

        const date = p?.createdAt
          ? new Date(p.createdAt).toLocaleDateString(undefined, {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })
          : "";

        const rawSizes = p?.size ?? p?.sizes ?? [];
        const sizeArr = Array.isArray(rawSizes)
          ? rawSizes
          : rawSizes
          ? [rawSizes]
          : [];

        const rawColors = p?.color ?? p?.colors ?? [];
        const colorArr = Array.isArray(rawColors)
          ? rawColors
          : rawColors
          ? [rawColors]
          : [];

        const tags = Array.isArray(p?.tags) ? p.tags : [];
        const tagSet = new Set(tags.map((t) => String(t).toLowerCase().trim()));
        const hasFileUploadTag =
          tagSet.has("file upload") || tagSet.has("featured file upload");

        const isPrintable = Boolean(p?.isPrintable) || hasFileUploadTag;

        const hasSizes = sizeArr.length > 0;
        const hasColors = colorArr.length > 0;

        return {
          raw: p,
          id,
          slug,
          title,
          price,
          img,
          color,
          date,
          _meta: { hasSizes, hasColors, isPrintable },
        };
      })
      .filter(Boolean);
  }, [wishlist]);

  // ✅ Remove from wishlist using thunk
  const removeItem = useCallback(
    async (productId) => {
      try {
        await dispatch(removeWishlistProduct(productId)).unwrap();
      } catch (err) {
        toast.error(
          err?.message || err?.response?.data?.message || "Failed to remove item"
        );
      }
    },
    [dispatch]
  );

  /* =====================================================
     ✅ PRINT PRICING RESOLVER (same idea as SingleProduct)
  ====================================================== */
  const resolvePrintPricing = useCallback(
    (p) => {
      if (!p) return null;

      const directObj =
        p?.printingPrice &&
        typeof p.printingPrice === "object" &&
        (p.printingPrice?.title || p.printingPrice?.printPrice)
          ? p.printingPrice
          : p?.printPricing &&
            typeof p.printPricing === "object" &&
            (p.printPricing?.title || p.printPricing?.printPrice)
          ? p.printPricing
          : p?.printPrice &&
            typeof p.printPrice === "object" &&
            (p.printPrice?.title || p.printPrice?.printPrice)
          ? p.printPrice
          : null;

      const idCandidates = [
        typeof p?.printingPrice === "string" ? p.printingPrice : null,
        typeof p?.printingPriceId === "string" ? p.printingPriceId : null,
        typeof p?.printPricingId === "string" ? p.printPricingId : null,
        typeof p?.printPrice === "string" ? p.printPrice : null,
        typeof p?.printPriceId === "string" ? p.printPriceId : null,
        typeof p?.pprice === "string" ? p.pprice : null,
        typeof p?.ppriceId === "string" ? p.ppriceId : null,
      ].filter(Boolean);

      const titleCandidates = [
        p?.printingType,
        p?.printType,
        p?.printPriceTitle,
        p?.printPricingTitle,
        p?.printPriceName,
        p?.printingPriceTitle,
      ]
        .map((x) => (x == null ? "" : String(x)))
        .map((x) => x.trim())
        .filter(Boolean);

      const byId = (id) => {
        if (!id) return null;
        return (
          (printPrices || []).find((x) => String(x?._id) === String(id)) || null
        );
      };

      const byTitle = (t) => {
        const s = String(t || "").trim().toLowerCase();
        if (!s) return null;
        return (
          (printPrices || []).find(
            (x) => String(x?.title || "").trim().toLowerCase() === s
          ) || null
        );
      };

      let record = directObj;

      if (!record) {
        for (const id of idCandidates) {
          record = byId(id);
          if (record) break;
        }
      }

      if (!record) {
        for (const t of titleCandidates) {
          record = byTitle(t);
          if (record) break;
        }
      }

      if (!record) return null;

      const preparePrice = Number(record?.preparePrice ?? 0) || 0;

      const oneSide = Number(record?.printPrice?.oneSide ?? 0) || 0;
      const twoSide = Number(record?.printPrice?.twoSide ?? 0) || 0;

      const discOne = Number(record?.printPriceDiscount?.oneSide ?? 0) || 0;
      const discTwo = Number(record?.printPriceDiscount?.twoSide ?? 0) || 0;

      const discountMinQty =
        record?.discountMinQty !== undefined && record?.discountMinQty !== null
          ? Number(record.discountMinQty)
          : null;

      return {
        id: record?._id || null,
        title: record?.title || "",
        preparePrice,
        discountMinQty: Number.isFinite(discountMinQty) ? discountMinQty : null,
        printPrice: { oneSide, twoSide },
        printPriceDiscount: { oneSide: discOne, twoSide: discTwo },
      };
    },
    [printPrices]
  );

  /* =====================================================
     ✅ VariantToast state (single add only)
  ====================================================== */
  const [toastState, setToastState] = React.useState({
    open: false,
    item: null,
    data: null,
  });

  const closeVariantToast = useCallback(() => {
    setToastState({ open: false, item: null, data: null });
  }, []);

  const addToCartDirect = useCallback(
    async (payload) => {
      await dispatch(addProdToCart(payload)).unwrap();
      await dispatch(fetchUserCart());
    },
    [dispatch]
  );

  const sizeById = useMemo(() => {
    const m = new Map();
    (allSizes || []).forEach((s) => {
      if (s?._id) m.set(String(s._id), s);
    });
    return m;
  }, [allSizes]);

  // ✅ helper: build a map { colorId -> [variantImageUrls...] } from product.variantImages
  const buildVariantImageMap = useCallback((p) => {
    const map = new Map();
    const arr = Array.isArray(p?.variantImages) ? p.variantImages : [];

    arr.forEach((v) => {
      const cId = String(v?.color?._id || v?.color || "").trim();
      if (!cId) return;

      const urls = Array.isArray(v?.images)
        ? v.images
            .map((im) => (typeof im === "string" ? im : im?.url))
            .filter(Boolean)
        : [];

      if (urls.length) map.set(cId, urls);
    });

    return map;
  }, []);

  const buildToastPayload = useCallback(
    (pRaw) => {
      const p = pRaw || {};

      const variantMap = buildVariantImageMap(p);

      const rawSizes = p?.size ?? p?.sizes ?? [];
      const sizeArr = Array.isArray(rawSizes)
        ? rawSizes
        : rawSizes
        ? [rawSizes]
        : [];

      const sizeLabels = sizeArr
        .map((s) => {
          if (typeof s === "string") {
            const fromStore = sizeById.get(String(s));
            const nm =
              fromStore?.name ||
              fromStore?.title ||
              fromStore?.labelName ||
              fromStore?.sizeName ||
              fromStore?.displayName ||
              fromStore?.label ||
              s;
            return stripMeasurements(nm);
          }
          const id = s?._id ? String(s._id) : null;
          const fromStore = id ? sizeById.get(id) : null;
          const finalObj = fromStore || s;
          const nm =
            finalObj?.name ||
            finalObj?.title ||
            finalObj?.labelName ||
            finalObj?.sizeName ||
            finalObj?.displayName ||
            finalObj?.label ||
            finalObj?.size ||
            "";
          return stripMeasurements(nm);
        })
        .map((x) => String(x || "").trim())
        .filter(Boolean);

      const rawColors = p?.color ?? p?.colors ?? [];
      const colorArr = Array.isArray(rawColors)
        ? rawColors
        : rawColors
        ? [rawColors]
        : [];

      const colors = colorArr
        .map((c) => {
          const fromStore =
            typeof c === "string"
              ? (allColors || []).find((x) => String(x?._id) === String(c)) ||
                (allColors || []).find(
                  (x) =>
                    String(x?.name || "").toLowerCase() ===
                    String(c).toLowerCase()
                ) ||
                null
              : c;

          const id = fromStore?._id || (typeof c === "string" ? c : null);
          const label =
            fromStore?.name ||
            fromStore?.title ||
            fromStore?.color ||
            (typeof c === "string" ? c : "Color");

          const hex = fromStore?.hex || fromStore?.code || fromStore?.value || null;

          // ✅ choose variant images for this color (if product has them)
          const variantImgs = id ? variantMap.get(String(id)) || [] : [];

          const swatchUrl =
            fromStore?.swatchImage?.url ||
            fromStore?.image?.url ||
            fromStore?.imageUrl ||
            "";

          // ✅ IMPORTANT:
          // imageUrl becomes the main "selected color image" used for variantImage
          const imageUrl = variantImgs?.[0] || swatchUrl || "";

          return {
            id,
            label,
            hex,
            imageUrl,
            allVariantImages: variantImgs,
          };
        })
        .filter((x) => x?.label);

      const tags = Array.isArray(p?.tags) ? p.tags : [];
      const tagSet = new Set(tags.map((t) => String(t).toLowerCase().trim()));
      const hasFileUploadTag =
        tagSet.has("file upload") || tagSet.has("featured file upload");

      const isPrintable = Boolean(p?.isPrintable) || hasFileUploadTag;

      const printSpec = {
        allowedExtensions:
          p?.printSpec?.allowedExtensions ?? ["pdf", "png", "jpg", "jpeg", "cdr", "ai"],
        maxFileSizeMB: Number(p?.printSpec?.maxFileSizeMB ?? 10),
        instructions: String(p?.printSpec?.instructions ?? ""),
      };

      const printPricing = resolvePrintPricing(p);

      return {
        sizes: Array.from(new Set(sizeLabels)),
        colors,
        isPrintable,
        printSpec,
        printPricing,
      };
    },
    [allColors, sizeById, resolvePrintPricing, buildVariantImageMap]
  );

  const getFallbackProductImage = useCallback((p, fallback = "") => {
    return (
      (Array.isArray(p?.images) && (p.images?.[0]?.url || p.images?.[0])) ||
      p?.image ||
      fallback ||
      ""
    );
  }, []);

  const needsToast = useCallback((toastData) => {
    const requiresSize = (toastData?.sizes?.length || 0) > 0;
    const requiresColor = (toastData?.colors?.length || 0) > 0;

    const printPricingEnabled = Boolean(toastData?.printPricing?.title);
    const requiresPrintSide = Boolean(printPricingEnabled);

    const requiresArtwork = Boolean(toastData?.isPrintable);

    return Boolean(requiresSize || requiresColor || requiresPrintSide || requiresArtwork);
  }, []);

  const addToCart = useCallback(
    async (item) => {
      const p = item?.raw || {};
      const toastData = buildToastPayload(p);

      try {
        if (needsToast(toastData)) {
          setToastState({ open: true, item, data: toastData });
          return;
        }

        const payload = {
          productId: item.id,
          quantity: 1,
          price: Number(item?.price ?? 0),
          variantImage: getFallbackProductImage(p, item.img),
        };

        await addToCartDirect(payload);
        toast.success(`Added: ${item.title}`);
      } catch (err) {
        toast.error(err?.message || "Failed to add to cart");
      }
    },
    [buildToastPayload, needsToast, getFallbackProductImage, addToCartDirect]
  );

  const toastProps = useMemo(() => {
    const item = toastState.item;
    const data = toastState.data;
    if (!toastState.open || !item || !data) return null;

    const sizesForToast = Array.isArray(data?.sizes) ? data.sizes : [];
    const colorsForToast = Array.isArray(data?.colors) ? data.colors : [];

    const requireSize = sizesForToast.length > 0;
    const requireColor = colorsForToast.length > 0;

    const printPricing = data?.printPricing || null;
    const requirePrintSide = Boolean(printPricing?.title);

    return {
      item,
      productTitle: item.title,
      sizes: sizesForToast,
      colors: colorsForToast,
      requireSize,
      requireColor,
      isPrintable: Boolean(data?.isPrintable),
      printSpec: data?.printSpec,
      requireArtwork: Boolean(data?.isPrintable),
      printPricing,
      requirePrintSide,
      initialPrintSide: "",
      qty: 1,
    };
  }, [toastState.open, toastState.item, toastState.data]);

  // ✅ IMPORTANT: use the selected color image as variantImage
  const onToastConfirm = useCallback(
    async ({ size, colorObj, artworkUploads, printInstructions, printSide } = {}) => {
      const item = toastProps?.item;
      if (!item?.id) return;

      const p = item?.raw || {};

      // ✅ pick image based on selected color (variantImages -> first), else fallback to product image
      const selectedColorImg =
        colorObj?.imageUrl ||
        colorObj?.allVariantImages?.[0] ||
        getFallbackProductImage(p, item.img) ||
        item.img ||
        "";

      const payload = {
        productId: item.id,
        quantity: 1,
        price: Number(item?.price ?? 0),
        variantImage: selectedColorImg,
      };

      // color
      if (colorObj?.id) payload.color = colorObj.id;

      // size label -> find id in store
      if (size) {
        const sNorm = stripMeasurements(size);
        const hit =
          (allSizes || []).find((x) => {
            const nm = stripMeasurements(
              x?.name ||
                x?.title ||
                x?.labelName ||
                x?.sizeName ||
                x?.displayName ||
                x?.label ||
                ""
            );
            return nm === sNorm;
          }) || null;

        if (hit?._id) payload.size = hit._id;
      }

      // uploads + instructions
      const safeUploads = Array.isArray(artworkUploads)
        ? artworkUploads
            .filter(Boolean)
            .map((u) => ({
              fileName:
                u?.fileName ||
                u?.original_filename ||
                u?.originalFilename ||
                (u?.public_id ? String(u.public_id).split("/").pop() : "artwork"),
              public_id: u?.public_id,
              url: u?.url,
            }))
            .filter((f) => f.public_id || f.url)
        : [];

      if (safeUploads.length) payload.uploadedFiles = safeUploads;
      if (printInstructions && String(printInstructions).trim()) {
        payload.instruction = String(printInstructions).trim();
      }

      // print pricing (simple: just add print unit to base)
      const printPricing = toastProps?.printPricing;
      const hasPrintPricing = Boolean(printPricing?.title && printSide);

      if (hasPrintPricing) {
        const base = Number(printPricing?.printPrice?.[printSide] ?? 0) || 0;
        payload.printSide = printSide;
        payload.printPricingTitle = printPricing?.title || "";
        payload.printUnitPrice = Number(base || 0);
        payload.price = Number(payload.price || 0) + Number(base || 0);
      }

      try {
        await addToCartDirect(payload);
        toast.success(`Added: ${item.title}`);
        closeVariantToast();
      } catch (e) {
        toast.error(e?.message || "Failed to add to cart");
      }
    },
    [
      toastProps,
      allSizes,
      getFallbackProductImage,
      addToCartDirect,
      closeVariantToast,
    ]
  );

  const onToastClose = useCallback(() => {
    closeVariantToast();
  }, [closeVariantToast]);

  const clearWishlist = () => {
    toast.info("Clear wishlist endpoint not added yet.");
    console.log("TODO clear wishlist");
  };

  const wishlistLink = useMemo(() => window.location.href, []);
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(wishlistLink);
      toast.success("Link copied");
    } catch (e) {
      const el = document.createElement("textarea");
      el.value = wishlistLink;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      toast.success("Link copied");
    }
  };

  const count = list.length;

  /* =========================
     ✅ MOBILE "APP" LOOK (tabs like the image)
     - purely UI; does not change your data
  ========================= */
  const [activeTab, setActiveTab] = useState("All");
  const appTabs = useMemo(
    () => ["All", "Stationery", "Print", "Apparel", "Gifts", "Office"],
    []
  );

  return (
    <div className="wl-page">
      {/* ✅ VARIANT TOAST (single add only) */}
      <VariantToast
        open={Boolean(toastProps)}
        onClose={onToastClose}
        productTitle={toastProps?.productTitle || "Product"}
        sizes={toastProps?.sizes || []}
        colors={toastProps?.colors || []}
        initialSize=""
        initialColorId={null}
        requireSize={Boolean(toastProps?.requireSize)}
        requireColor={Boolean(toastProps?.requireColor)}
        isPrintable={Boolean(toastProps?.isPrintable)}
        printSpec={toastProps?.printSpec}
        requireArtwork={Boolean(toastProps?.requireArtwork)}
        printPricing={toastProps?.printPricing}
        requirePrintSide={Boolean(toastProps?.requirePrintSide)}
        initialPrintSide={toastProps?.initialPrintSide || ""}
        qty={1}
        onConfirm={onToastConfirm}
      />

      {/* =======================================================
          ✅ MOBILE APP WISHLIST (like your image – MIDDLE screen)
          - Only visible on mobile via CSS
      ======================================================= */}
      <section className="wl-appOnly">
        <header className="wl-appHead">
          <button
            type="button"
            className="wl-appBack2"
            onClick={() => navigate(-1)}
            aria-label="Back"
          >
            ←
          </button>

          <div className="wl-appHeadTitle">My Wishlist</div>

          <div className="wl-appHeadRight">
            <span className="wl-appCount">{count}</span>
          </div>
        </header>

        <div className="wl-appTabs">
          {appTabs.map((t) => (
            <button
              key={t}
              type="button"
              className={`wl-tab ${activeTab === t ? "active" : ""}`}
              onClick={() => setActiveTab(t)}
            >
              {t}
            </button>
          ))}
        </div>

        <div className="wl-appGridWrap">
          {isLoading ? (
            <div className="wl-appSkelGrid">
              {Array.from({ length: 6 }).map((_, i) => (
                <div className="wl-appCard wl-appCard--skel" key={i}>
                  <div className="wl-skelBox wl-appSkelImg" />
                  <div className="wl-appSkelBody">
                    <div className="wl-skelBox wl-appSkelLine1" />
                    <div className="wl-skelBox wl-appSkelLine2" />
                  </div>
                </div>
              ))}
            </div>
          ) : list.length === 0 ? (
            <div className="wl-appEmpty">
              <div className="wl-appEmptyTitle">Your wishlist is empty</div>
              <Link className="wl-appEmptyBtn" to="/product">
                Go to Store
              </Link>
            </div>
          ) : (
            <div className="wl-appGrid">
              {list.map((item) => (
                <div className="wl-appCard" key={item.id}>
                  {/* heart remove */}
                  <button
                    type="button"
                    className="wl-appHeart"
                    onClick={() => removeItem(item.id)}
                    aria-label="Remove from wishlist"
                    title="Remove"
                  >
                    ♥
                  </button>

                  <Link to={`/product/${item.slug}`} className="wl-appImg">
                    <img src={item.img} alt={item.title} />
                  </Link>

                  <div className="wl-appBody">
                    <Link to={`/product/${item.slug}`} className="wl-appName">
                      {item.title}
                    </Link>

                    <div className="wl-appMeta">
                      <div className="wl-appPrice">
                        {Number.isFinite(item.price) ? formatUGX(item.price) : "UGX 0"}
                      </div>

                      {item.color ? (
                        <div className="wl-appColor">• {item.color}</div>
                      ) : null}
                    </div>

                    <button
                      type="button"
                      className="wl-appAdd"
                      onClick={() => addToCart(item)}
                    >
                      Add to Cart
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ✅ MOBILE APP HEADER (your old one) - keep, but hidden in new mobile design */}
      <section className="wl-appTop">
        <div className="wl-appBar">
          <button
            type="button"
            className="wl-appBack"
            onClick={() => navigate(-1)}
            aria-label="Back"
          >
            ‹
          </button>

          <div className="wl-appCenter">
            <div className="wl-appTitle">Wishlist</div>
          </div>

          <div className="wl-appRight">
            <span className="wl-appBadge" title="Items in wishlist">
              {count}
            </span>
          </div>
        </div>
      </section>

      {/* ✅ DESKTOP HERO */}
      <div className="wl-hero">
        <h1 className="wl-title">Wishlist</h1>
        <p className="wl-bread">
          <Link to="/" className="wl-breadLink">
            Home
          </Link>{" "}
          <span>/</span> Wishlist
        </p>
      </div>

      {/* TABLE CARD */}
      <div className="wl-card">
        {/* Header row */}
        <div className="wl-head">
          <div className="wl-col wl-col--product">Product</div>
          <div className="wl-col wl-col--price">Price</div>
          <div className="wl-col wl-col--date">Date Added</div>
          <div className="wl-col wl-col--stock">View product</div>
          <div className="wl-col wl-col--action"></div>
        </div>

        {/* Body */}
        <div className="wl-body">
          {isLoading ? (
            <WishlistSkeleton rows={5} />
          ) : list.length === 0 ? (
            <div className="wl-empty">
              <p>Your wishlist is empty.</p>
            </div>
          ) : (
            list.map((item) => (
              <div className="wl-row" key={item.id}>
                {/* Product */}
                <div className="wl-col wl-col--product">
                  <button
                    className="wl-remove"
                    type="button"
                    aria-label="Remove"
                    onClick={() => removeItem(item.id)}
                    title="Remove"
                  >
                    ×
                  </button>

                  <div className="wl-prod">
                    <div className="wl-img">
                      <img src={item.img} alt={item.title} />
                    </div>

                    <div className="wl-prodText">
                      <div className="wl-name">{item.title}</div>

                      {item.color ? (
                        <div className="wl-color">
                          Color : <span>{item.color}</span>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>

                {/* Price (UGX) */}
                <div className="wl-col wl-col--price">
                  {Number.isFinite(item.price) ? formatUGX(item.price) : "--"}
                </div>

                {/* Date */}
                <div className="wl-col wl-col--date">{item.date || "--"}</div>

                {/* View product */}
                <div className="wl-col wl-col--stock">
                  <Link className="wl-view" to={`/product/${item.slug}`}>
                    View product
                  </Link>
                </div>

                {/* Action */}
                <div className="wl-col wl-col--action">
                  <button
                    className="wl-btn"
                    type="button"
                    onClick={() => addToCart(item)}
                  >
                    Add to Cart
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Bottom actions */}
        <div className="wl-bottom">
          <div className="wl-linkRow">
            <span className="wl-linkLabel">Wishlist link:</span>

            <div className="wl-linkInput" title={wishlistLink}>
              {wishlistLink}
            </div>

            <button
              className="wl-btn wl-btn--ghost"
              type="button"
              onClick={copyLink}
            >
              Copy Link
            </button>
          </div>

          <div className="wl-actions">
            <button className="wl-clear" type="button" onClick={clearWishlist}>
              Clear Wishlist
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Wishlist;